import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { concatMap, filter, map, Observable, take } from 'rxjs';

import { Command } from '@ninety/mobile/platform/auth';
import { SegmentTrackEvent } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';
import { SegmentActions } from '../analytics/segment/segment.actions';

import { CommandRunner } from './command-runner.service';

type SignedInNotification = Extract<Command, { do: 'notifyAppSignedIn' }>;

/** A type guard, because a boolean predicate in `filter` does not narrow the union for `map`. */
function isSignIn(command: SignedInNotification): command is Extract<SignedInNotification, { via: 'signIn' }> {
  return command.via === 'signIn';
}

/**
 * What the auth machine reports to Segment.
 *
 * Separate from `SessionAppEffects` because every slice tracks Segment from a dedicated
 * `*AnalyticsEffects`.
 *
 * Note this is Segment, not the auth machine's own telemetry. Everything about *how* authentication
 * behaves — transitions, failures, sign-out reasons, durations — goes to Datadog RUM through
 * `AuthTelemetryReporter`, which derives it from `(before, after, event, commands)` so no call site
 * can forget to report. This file carries the product events.
 */
@Injectable()
export class SessionAnalyticsEffects {
  private readonly commands = inject(CommandRunner);
  private readonly actions$ = inject(Actions);

  /**
   * Someone signed in, meaning they went through the sign-in screen.
   *
   * **A restored session is not a login**, and this reported one as though it were: `notifyAppSignedIn`
   * is issued for a finished Grant *and* for a cold start that rebuilt a session from the Durable Root,
   * so every launch by an already-signed-in person booked a `login` — and so did a Company Switch,
   * because the reload it orders makes the next launch a cold start. `via: 'signIn'` is the Grant, and
   * it is the only one that counts here.
   *
   * The way in is a param rather than four events, so the total and the breakdown are one number grouped
   * two ways. `loginType` and not `source`, which in Segment names the client that sent the event.
   */
  readonly trackSignIn$ = createEffect(() =>
    this.commands.issued('notifyAppSignedIn').pipe(
      filter(isSignIn),
      this.onceIdentified(command =>
        SegmentActions.track({ event: SegmentTrackEvent.LOGIN, params: { loginType: command.source } })
      )
    )
  );

  /**
   * A launch that rebuilt a session from the Durable Root, so nobody was asked to sign in.
   *
   * The counterpart to {@link trackSignIn$} and the reason that one had to stop counting restores: read
   * against each other they answer whether the auth work landed — a rising restore rate against a flat
   * login count is people coming back without re-authenticating, and pooled into one event that is
   * invisible.
   *
   * `via: 'restore'` is every cold start that found a session, which is deliberately the whole of it: a
   * launch either had to ask or it did not, and no other refresh reaches this Command. A resume is not
   * one — the app was already running — so this counts returns to a cold app, not foregrounds.
   */
  readonly trackSessionRestored$ = createEffect(() =>
    this.commands.issued('notifyAppSignedIn').pipe(
      // A plain predicate rather than a guard: nothing is read off the command, so there is nothing to
      // narrow it for.
      filter(command => command.via === 'restore'),
      this.onceIdentified(() => SegmentActions.track({ event: SegmentTrackEvent.SESSION_RESTORED }))
    )
  );

  /**
   * Hold an event until the identity that enriches it exists.
   *
   * **Both of these fire at the moment a session appears, which is before the app knows whose it is.**
   * `trackGenericEvent$` merges `selectSegmentContactTraits` into every payload, and those traits come
   * from the current person and company user — neither of which is loaded yet, because the very Command
   * these effects read is what triggers loading them. Tracked at that moment, a `login` ships
   * `currentUserId: ''`, `currentPersonId: ''`, `currentUserCompany: ''` and `company: {}`.
   *
   * Worse than blank: `selectSegmentContactTraits` falls back to `getCommonTraits({} as UserModel, …)`,
   * so `currentUserRoleCode: '0004'` and `currentUserRoleName: 'Observer'` are *populated* — the event
   * asserts a role the person may not have rather than admitting it does not know.
   *
   * `appAndCompanyReady` is the existing answer to "identity is known": `AppEffects` dispatches it when
   * the person, users, teams, language and company users have all loaded, and `SegmentEffects.initializeUser$`
   * already keys `identify()` off it. Waiting for the same action puts the `identify` before the `track`,
   * so the event lands against a user Segment has been told about.
   *
   * `take(1)` per event and `concatMap` rather than `switchMap`: a second notification arriving while one
   * waits should queue, not cancel. Dropping a login is worse than delaying one. There is no timeout, and
   * that is deliberate — a launch that never reaches `appAndCompanyReady` is one the app cannot run, and
   * the machine ends it as `sessionUnusable`. Reporting a login for it would be reporting a session
   * nobody got.
   *
   * The cost to accept: `url` and `urlPath` are sampled when the event fires, so a `login` now carries the
   * screen the person landed on rather than `/login`.
   */
  private onceIdentified<T>(toAction: (value: T) => Action) {
    return (source: Observable<T>): Observable<Action> =>
      source.pipe(
        concatMap(value =>
          this.actions$.pipe(
            ofType(appActions.appAndCompanyReady),
            take(1),
            map(() => toAction(value))
          )
        )
      );
  }
}
